import { schema } from 'normalizr';
import { createAction } from 'redux-api-middleware';
import { buildQueryString, handleResponse, handleError } from '../utils';

export const FETCH_PRODUCT_REQUEST = 'FETCH_PRODUCT_REQUEST';
export const FETCH_PRODUCT_SUCCESS = 'FETCH_PRODUCT_SUCCESS';
export const FETCH_PRODUCT_FAILURE = 'FETCH_PRODUCT_FAILURE';
export const FETCH_PRODUCTS_REQUEST = 'FETCH_PRODUCTS_REQUEST';
export const FETCH_PRODUCTS_SUCCESS = 'FETCH_PRODUCTS_SUCCESS';
export const FETCH_PRODUCTS_UPDATE_SUCCESS = 'FETCH_PRODUCTS_UPDATE_SUCCESS';
export const FETCH_PRODUCTS_FAILURE = 'FETCH_PRODUCTS_FAILURE';
export const CREATE_PRODUCT_REQUEST = 'CREATE_PRODUCT_REQUEST';
export const CREATE_PRODUCT_SUCCESS = 'CREATE_PRODUCT_SUCCESS';
export const CREATE_PRODUCT_FAILURE = 'CREATE_PRODUCT_FAILURE';
export const EDIT_PRODUCT_REQUEST = 'EDIT_PRODUCT_REQUEST';
export const EDIT_PRODUCT_SUCCESS = 'EDIT_PRODUCT_SUCCESS';
export const EDIT_PRODUCT_FAILURE = 'EDIT_PRODUCT_FAILURE';
export const UPDATE_PRODUCTS_PAGE = 'UPDATE_PRODUCTS_PAGE';
export const PRODUCT_PAGE_SIZE = 20;

export const productSchema = new schema.Entity('products', {}, { idAttribute: 'tokenId' });

/**
 * Returns a redux api middleware action that makes a request for a collection of products.
 *
 * @param {any} tokenId - Query parameters.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchProduct = tokenId =>
  createAction({
    endpoint: `${window.config.apiUrl}/products/${tokenId}`,
    method: 'GET',
    types: [
      { type: FETCH_PRODUCT_REQUEST },
      {
        type: FETCH_PRODUCT_SUCCESS,
        payload: (action, state, res) => handleResponse(productSchema, action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_PRODUCT_FAILURE, payload: handleError },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request for a collection of products.
 *
 * @param {object} parameters - Query parameters.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchProducts = ({ start = 0, count = PRODUCT_PAGE_SIZE, owner = null } = {}) =>
  createAction({
    endpoint: `${window.config.apiUrl}/products?${buildQueryString({ start, count, owner })}`,
    method: 'GET',
    types: [
      { type: FETCH_PRODUCTS_REQUEST },
      {
        type: start === 0 ? FETCH_PRODUCTS_SUCCESS : FETCH_PRODUCTS_UPDATE_SUCCESS,
        payload: (action, state, res) => handleResponse([productSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_PRODUCTS_FAILURE, payload: handleError },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request to create a product.
 *
 * @param {object} values - New product field values.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createProduct = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/products`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_PRODUCT_REQUEST },
      {
        type: CREATE_PRODUCT_SUCCESS,
        payload: (action, state, res) => handleResponse(productSchema, action, state, res),
      },
      { type: CREATE_PRODUCT_FAILURE, payload: handleError },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request to create a product.
 *
 * @param {object} tokenId - Product token id.
 * @param {object} values - New product field values.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const editProduct = (tokenId, values) =>
  createAction({
    endpoint: `${window.config.apiUrl}/products/${tokenId}`,
    method: 'PUT',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_PRODUCT_REQUEST },
      {
        type: EDIT_PRODUCT_SUCCESS,
        payload: (action, state, res) => handleResponse(productSchema, action, state, res),
      },
      { type: EDIT_PRODUCT_FAILURE, payload: handleError },
    ],
  });

export const updateProductsPage = page => async dispatch => {
  dispatch({ type: UPDATE_PRODUCTS_PAGE, payload: page });

  const start = (page - 1) * PRODUCT_PAGE_SIZE;
  const count = PRODUCT_PAGE_SIZE;
  dispatch(fetchProducts({ start, count }));
};
